import { useCallback, useEffect } from 'react';
import { useColorScheme } from '@mui/material/styles';
import { IconButton, Tooltip } from '@mui/material';
import { FaSun, FaMoon, FaDesktop } from 'react-icons/fa';

/**
 * Set once the visitor picks a mode by hand. While it is absent we are free to
 * keep re-deriving the mode from the environment on every load; once it is set
 * we never touch their choice again.
 */
const MANUAL_KEY = 'dv-color-mode-manual';

const DARK_FROM_HOUR = 19;
const DARK_UNTIL_HOUR = 6;

function isNightTime(): boolean {
  const hour = new Date().getHours();
  return hour >= DARK_FROM_HOUR || hour < DARK_UNTIL_HOUR;
}

function hasManualChoice(): boolean {
  try {
    return localStorage.getItem(MANUAL_KEY) === '1';
  } catch {
    return false;
  }
}

function setManualChoice(manual: boolean): void {
  try {
    if (manual) localStorage.setItem(MANUAL_KEY, '1');
    else localStorage.removeItem(MANUAL_KEY);
  } catch {
    /* private browsing: fall back to per-session behaviour */
  }
}

/**
 * Resolution order: a manual choice wins; otherwise follow the browser's
 * `prefers-color-scheme`; and only when the browser expresses *no* preference
 * do we fall back to local time (dark 19:00–06:00).
 *
 * Heads up: modern browsers report `light` rather than `no-preference` when the
 * OS was never configured (`no-preference` was dropped from the media-query
 * spec), so in practice the time branch fires only on older or unusual
 * browsers. Most visitors get pure OS-preference behaviour.
 *
 * The same cascade runs inline in index.html before first paint, to avoid a
 * flash of the wrong theme. Keep the two in sync.
 */
export function useAutoColorMode(): void {
  const { mode, setMode } = useColorScheme();

  const evaluate = useCallback(() => {
    if (!mode || hasManualChoice()) return;

    if (!window.matchMedia('(prefers-color-scheme: no-preference)').matches) {
      // The browser has an opinion, so defer to it.
      if (mode !== 'system') setMode('system');
      return;
    }

    const wanted = isNightTime() ? 'dark' : 'light';
    if (mode !== wanted) setMode(wanted);
  }, [mode, setMode]);

  useEffect(() => {
    evaluate();
    // Re-derive when the tab is brought back to the foreground, so a session
    // left open across sunset picks up the change.
    const onVisible = () => {
      if (document.visibilityState === 'visible') evaluate();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [evaluate]);
}

const NEXT_MODE = { light: 'dark', dark: 'system', system: 'light' } as const;

const LABEL = {
  light: 'Switch to dark theme',
  dark: 'Switch to automatic theme',
  system: 'Switch to light theme',
} as const;

/** Cycles light → dark → automatic. */
export function ColorModeToggle({ onDark = false }: { onDark?: boolean }) {
  const { mode, setMode } = useColorScheme();

  // Undefined until MUI has read storage; render a placeholder so the toolbar
  // doesn't reflow when it settles.
  const current = mode ?? 'system';

  const handleClick = () => {
    const next = NEXT_MODE[current];
    setManualChoice(next !== 'system');
    setMode(next);
  };

  const Icon = current === 'light' ? FaSun : current === 'dark' ? FaMoon : FaDesktop;

  return (
    <Tooltip title={LABEL[current]} enterDelay={600}>
      <IconButton
        onClick={handleClick}
        aria-label={LABEL[current]}
        size="small"
        sx={{
          width: 34,
          height: 34,
          fontSize: '0.875rem',
          color: onDark ? 'rgba(255,255,255,0.72)' : 'text.secondary',
          transition: 'color 0.2s, background 0.2s',
          '&:hover': {
            color: onDark ? '#fff' : 'text.primary',
            bgcolor: onDark ? 'rgba(255,255,255,0.08)' : 'action.hover',
          },
        }}
      >
        <Icon />
      </IconButton>
    </Tooltip>
  );
}
